import { Box, Grid, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { useStatistics } from 'app/hooks/useStatistics';
import { Button, TextField, Typography } from 'app/shared';
import { IReportStatus } from 'generated/axios';
import { t } from 'i18next';
import { isEmpty } from 'lodash';
import { getDrugTestTypeLabel } from '../report/substances';

interface Props {
  id: string;
}

export const MedicalCenterStatisticsTab = ({ id }: Props) => {
  const { statistics, monthlyStatistics, monthlyOverviewForm } = useStatistics(id);
  const { bindSelect, submit } = monthlyOverviewForm;

  const getReportStatus = (status: IReportStatus | undefined) => {
    switch (status) {
      case 'draft':
        return t('report.status.draft');
      case 'processed':
        return t('report.status.processed');
      case 'confirmed':
        return t('report.status.confirmed');
      case 'error':
        return t('report.status.error');
      default: //in case of new non-translated values
        return status ?? '';
    }
  };

  const getAcceptableYears = () => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: currentYear - 2025 + 1 }, (_, i) => 2025 + i);
  };

  return (
    <Grid container>
      <Grid size={{ sm: 12, md: 6 }}>
        <Box display={'flex'} justifyContent={'center'} alignItems={'center'}>
          <Typography detailTitle>{t('medicalCenter.totalStatistics')}</Typography>
        </Box>
        <TableContainer sx={{ display: 'flex', justifyContent: 'center' }}>
          <Table sx={{ width: 500 }}>
            <TableHead>
              <TableRow>
                <TableCell width={300}>
                  <Typography>{t('common.type')}</Typography>
                </TableCell>
                <TableCell width={100}>
                  <Typography>{t('common.total')}</Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* active doctors */}
              <TableRow>
                <TableCell>
                  <Typography variant="body2">{t('medicalCenter.activeDoctors')}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{statistics?.active_doctors}</Typography>
                </TableCell>
              </TableRow>
              {/* total patients */}
              <TableRow>
                <TableCell>
                  <Typography variant="body2">{t('medicalCenter.totalPatients')}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{statistics?.active_doctors}</Typography>
                </TableCell>
              </TableRow>
              {/* total reports */}
              <TableRow>
                <TableCell>
                  <Typography variant="body2">{t('medicalCenter.totalReports')}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{statistics?.active_doctors}</Typography>
                </TableCell>
              </TableRow>
              {Object.entries(statistics?.reports_by_status ?? []).map(([status, count]) => (
                <TableRow>
                  <TableCell>
                    <Typography variant="body2">{getReportStatus(status as IReportStatus)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{count}</Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
      <Grid size={{ sm: 12, md: 6 }} textAlign={'center'}>
        <Typography detailTitle>{t('medicalCenter.monthlyStatistics')}</Typography>
        <Grid container spacing={3} alignItems={'center'}>
          <Grid size={4}>
            <TextField size="small" label={t('common.month')} {...bindSelect({ field: 'month', defaultVal: 1 })}>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((month) => (
                <MenuItem key={month} value={month}>
                  {month}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={4}>
            <TextField
              size="small"
              label={t('common.year')}
              {...bindSelect({ field: 'year', defaultVal: new Date().getFullYear() })}
            >
              {getAcceptableYears().map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={2}>
            <Button fullWidth onClick={submit}>
              {t('actions.search')}
            </Button>
          </Grid>
        </Grid>
        {!isEmpty(monthlyStatistics) && (
          <TableContainer sx={{ display: 'flex', justifyContent: 'center' }}>
            <Table sx={{ width: 500 }}>
              <TableHead>
                <TableRow>
                  <TableCell width={300}>
                    <Typography>{t('common.type')}</Typography>
                  </TableCell>
                  <TableCell width={100}>
                    <Typography>{t('common.total')}</Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>
                    <Typography variant="body2">{t('medicalCenter.draftReports')}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{monthlyStatistics?.draft_reports}</Typography>
                  </TableCell>
                </TableRow>
                {Object.entries(monthlyStatistics?.reports_by_test_type ?? []).map(([type, count]) => (
                  <TableRow>
                    <TableCell>
                      <Typography variant="body2">{getDrugTestTypeLabel(type)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{count}</Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Grid>
    </Grid>
  );
};
